<script setup lang="ts">
import SelectComponent from '@/components/select/SelectComponent.vue'
import {
    dataModifyAddressHappy,
    dataModifyAddressHappyEmpty
} from '@/happyTheme/event/dashboard/address/dataModifyAddressHappy'
import {
    showModalAddressHappy,
    showModalAddressHappyTransition
} from '@/happyTheme/event/dashboard/address/showModalAddressHappy'
import type { IDataModifyAddressHappy } from '@/happyTheme/interfaces/dashboard/address/IDataModifyAddressHappy'
import { AddressService } from '@/services/AddressService'
import { configDeliveryZones } from '@/storage/setting/configDeliveryZones'
import type { DeliveryZone } from '@/types/stores/setting/DeliveryZoneInterface'
import { computed, onMounted, ref } from 'vue'

const errorToggle = ref(false)
const isSend = ref(false)
const company = ref(
    dataModifyAddressHappy.value.company ? dataModifyAddressHappy.value.company : ''
)
const city = ref(
    dataModifyAddressHappy.value
        ? dataModifyAddressHappy.value
        : dataModifyAddressHappyEmpty
)
const zone = ref(
    dataModifyAddressHappy.value.zone_id
        ? dataModifyAddressHappy.value
        : dataModifyAddressHappyEmpty
)

const address1 = ref(
    dataModifyAddressHappy.value.address_1 ? dataModifyAddressHappy.value.address_1 : ''
)
const address2 = ref(
    dataModifyAddressHappy.value.address_2 ? dataModifyAddressHappy.value.address_2 : ''
)
const defaultAddress = ref(
    dataModifyAddressHappy.value.default ? dataModifyAddressHappy.value.default : false
)

const errorEmptyForm = ref({
    city: false,
    zone: false,
    address1: false
})

const zones = computed(() => {
    const zones = configDeliveryZones.value.reduce<DeliveryZone[]>((acc, zone) => {
        if (!acc.some((z) => z.zone_id === zone.zone_id)) {
            acc.push(zone)
        }
        return acc
    }, [])
    return zones
})

const citys = computed(() => {
    if (!zone.value.zone_id) return []

    return configDeliveryZones.value
        .filter((z) => z.zone_id === zone.value.zone_id)
        .map((zone) => zone)
})

function toggleShowModalAddressHappy() {
    showModalAddressHappyTransition.value = !showModalAddressHappyTransition.value
    setTimeout(() => {
        showModalAddressHappy.value = !showModalAddressHappy.value
        dataModifyAddressHappy.value = dataModifyAddressHappyEmpty
    }, 300)
}

function selectRegion(selectedObject: IDataModifyAddressHappy) {
    zone.value = selectedObject
    city.value = dataModifyAddressHappyEmpty
}

function selectedCity(selectedObject: IDataModifyAddressHappy) {
    city.value = selectedObject ? selectedObject : dataModifyAddressHappyEmpty
}

function sendError(error: boolean) {
    errorToggle.value = error
    setTimeout(() => {
        errorToggle.value = false
    }, 500)
}

async function addAddress() {
    if (!city.value.city) {
        errorEmptyForm.value.city = true
    }
    if (!zone.value.zone_id) {
        errorEmptyForm.value.zone = true
    }
    if (address1.value.length < 1) {
        errorEmptyForm.value.address1 = true
    }
    if (
        errorEmptyForm.value.city ||
        errorEmptyForm.value.zone ||
        errorEmptyForm.value.address1
    ) {
        setTimeout(() => {
            errorEmptyForm.value.city = false
            errorEmptyForm.value.zone = false
            errorEmptyForm.value.address1 = false
        }, 2000)
        return
    }

    const data = {
        company: company.value,
        city: city.value,
        zone: zone.value,
        address: address1.value,
        address_2: address2.value,
        default: defaultAddress.value
    }

    const addressService = new AddressService()
    const response = await addressService.addAddress(data)

    if (response.class === 'success') {
        isSend.value = true
        toggleShowModalAddressHappy()
    }
}

async function updateAddress() {
    if (!city.value.city) {
        errorEmptyForm.value.city = true
    }
    if (!zone.value.zone_id) {
        errorEmptyForm.value.zone = true
    }
    if (address1.value.length < 1) {
        errorEmptyForm.value.address1 = true
    }
    if (
        errorEmptyForm.value.city ||
        errorEmptyForm.value.zone ||
        errorEmptyForm.value.address1
    ) {
        setTimeout(() => {
            errorEmptyForm.value.city = false
            errorEmptyForm.value.zone = false
            errorEmptyForm.value.address1 = false
        }, 2000)
        return
    }

    const data = {
        address_id: dataModifyAddressHappy.value.address_id,
        company: company.value,
        city: city.value,
        zone: zone.value,
        address_1: address1.value,
        address_2: address2.value,
        default: defaultAddress.value
    }

    const addressService = new AddressService()
    const response = await addressService.updateAddress(data)

    if (response.class === 'success') {
        isSend.value = true
        toggleShowModalAddressHappy()
    }
}

onMounted(() => {
    isSend.value = false
})
</script>

<template>
    <section class="section-modal-address-happy">
        <section
            class="section-overlay-modal-address-happy"
            @click="toggleShowModalAddressHappy()"
        ></section>

        <transition
            enter-active-class="transition-all duration-300 ease-in-out"
            enter-from-class="opacity-0 -translate-y-4"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition-all duration-300 ease-in-out"
            leave-from-class="opacity-100 translate-x-0"
            leave-to-class="opacity-0 -translate-y-4"
        >
            <section
                class="section-parent-modal-address-happy"
                v-if="showModalAddressHappyTransition"
            >
                <section class="section-content-modal-address-happy">
                    <div class="div-header-modal-address-happy">
                        <h3 v-if="dataModifyAddressHappy.address_id">
                            Modification d'adresse
                        </h3>
                        <h3 v-else>Ajouter une adresse</h3>
                        <p class="text-required-address-happy">*Obligatoire</p>
                    </div>

                    <section class="section-form-modal-address-happy">
                        <div class="div-group-input-modal-address-happy">
                            <div class="div-label-required-modal-address-happy">
                                <span class="span-required-address-happy">*</span>
                                <label for="postalCode" class="label-address-happy"
                                    >Région</label
                                >
                            </div>

                            <SelectComponent
                                :options="zones"
                                value-key="zone"
                                :placeholder="'Sélectionner une région'"
                                required
                                :selected="zone"
                                @change="selectRegion"
                                :error="errorToggle || errorEmptyForm.zone"
                                theme="happy"
                            />
                        </div>

                        <div class="div-group-input-modal-address-happy">
                            <div class="div-label-required-modal-address-happy">
                                <span class="span-required-address-happy">*</span>
                                <label for="name_city" class="label-address-happy"
                                    >Ville</label
                                >
                            </div>
                            <SelectComponent
                                :options="citys"
                                display-key="city"
                                :placeholder="'Sélectionner une ville'"
                                :selected="city"
                                :disabled="!zone.zone_id"
                                @change="selectedCity"
                                @send-error="sendError"
                                :error="errorEmptyForm.city"
                                theme="happy"
                            />
                        </div>

                        <div class="div-group-input-modal-address-happy-company">
                            <div class="div-label-required-modal-address-happy">
                                <label for="company" class="label-address-happy">
                                    Société
                                </label>
                            </div>
                            <input
                                class="input-address-happy"
                                type="text"
                                id="company"
                                v-model="company"
                                placeholder="Société"
                            />
                        </div>

                        <div class="div-group-input-modal-address-happy">
                            <div class="div-label-required-modal-address-happy">
                                <span class="span-required-address-happy">*</span>
                                <label for="address_1" class="label-address-happy"
                                    >Adresse 1</label
                                >
                            </div>
                            <input
                                :class="{ shake: errorEmptyForm.address1 }"
                                class="input-address-happy"
                                type="text"
                                id="address_1"
                                v-model="address1"
                                placeholder="Adresse 1"
                            />
                        </div>

                        <div class="div-group-textarea-modal-address-happy">
                            <label for="address_2" class="pl-4">
                                Information complémentaire
                                <span class="text-sm">(optionnel)</span>
                            </label>
                            <textarea
                                class="textarea-address-happy"
                                id="address_2"
                                v-model="address2"
                                placeholder="Information complémentaire (optionnel)"
                            ></textarea>
                        </div>

                        <div class="div-group-input-default-modal-address-happy">
                            <p class="label-address-default-happy">
                                Adresse par défaut :
                            </p>
                            <div class="div-radio-button-modal-address-happy">
                                <label for="default_address">oui</label>
                                <input
                                    type="radio"
                                    id="default_address"
                                    v-model="defaultAddress"
                                    value="true"
                                />
                            </div>
                            <div class="div-radio-button-modal-address-happy">
                                <label for="default_address_no">non</label>
                                <input
                                    type="radio"
                                    id="default_address_no"
                                    v-model="defaultAddress"
                                    value="false"
                                    checked
                                />
                            </div>
                        </div>
                    </section>

                    <div class="div-button-modal-address-happy">
                        <button
                            class="button-close-modal-address-happy"
                            @click="toggleShowModalAddressHappy()"
                        >
                            Fermer
                        </button>
                        <button
                            v-if="!dataModifyAddressHappy.address_id && isSend === false"
                            class="button-submit-modal-address-happy"
                            @click="addAddress()"
                        >
                            Enregistrer
                        </button>
                        <button
                            v-else-if="
                                dataModifyAddressHappy.address_id && isSend === false
                            "
                            class="button-submit-modal-address-happy"
                            @click="updateAddress()"
                        >
                            Modifier
                        </button>
                    </div>
                </section>
            </section>
        </transition>
    </section>
</template>

<style scoped>
.section-modal-address-happy {
    @apply fixed flex justify-center top-0 left-0 w-full h-full z-30;
}
.section-overlay-modal-address-happy {
    @apply w-full h-full bg-text-dark-happy-light bg-opacity-30;
}

.section-parent-modal-address-happy {
    @apply absolute z-40  mt-12 px-2 flex justify-center;
}
.section-content-modal-address-happy {
    @apply w-full border-x-2 border-b-2 border-border-gray-happy-light gap-3 rounded-md happy-dark:bg-text-dark-happy-light happy-light:bg-bg-happy-light shadow happy-light:shadow-text-dark-happy-light happy-dark:shadow-bg-happy-light;
    @apply happy-light:shadow-text-dark-happy-light;
    @apply happy-dark:shadow-bg-happy-light;
}

.div-header-modal-address-happy {
    @apply relative flex justify-between p-4 text-text-dark-happy-light bg-border-gray-happy-light rounded-t-sm font-semibold border-t border-x border-border-gray-happy-light;
}
.text-required-address-happy {
    @apply text-border-red-happy-light absolute top-2 right-2 font-semibold text-xs;
}
.section-form-modal-address-happy {
    @apply w-full flex flex-col gap-3 p-4  happy-light:text-text-dark-happy-light happy-dark:text-bg-happy-light;
}
.div-group-input-modal-address-happy-company {
    @apply flex flex-col gap-1 w-full;
}
.div-group-input-modal-address-happy {
    @apply flex flex-col gap-1;
}
.div-group-input-default-modal-address-happy {
    @apply flex gap-2 w-full justify-end pt-2;
}

.div-group-textarea-modal-address-happy {
    @apply flex flex-col gap-1;
}

.label-address-default-happy {
    @apply happy-dark:text-bg-happy-light happy-light:text-text-dark-happy-light font-medium;
}

.div-radio-button-modal-address-happy {
    @apply flex items-center gap-1;
}

.div-label-required-modal-address-happy {
    @apply flex flex-row pl-4;
}

.span-required-address-happy {
    @apply text-border-red-happy-light font-semibold;
}
/* .label-address-happy {
    @apply text-sm;
} */

.input-address-happy {
    @apply w-full min-h-8 py-1 border-banner-gray-component-happy-light rounded-md border-2 text-text-dark-happy-light px-3  text-center;
}
.input-address-happy:focus {
    @apply border-button-yellow-happy-light outline-none;
}

.textarea-address-happy {
    @apply w-full min-h-24 border-banner-gray-component-happy-light rounded-md border-2 text-text-dark-happy-light px-3 py-0.5 text-center;
}
.textarea-address-happy:focus {
    @apply border-button-yellow-happy-light outline-none;
}

.option-zones-happy {
    @apply pr-4;
}

.div-button-modal-address-happy {
    @apply w-full flex justify-end gap-2 p-3 text-text-dark-happy-light;
}

.button-close-modal-address-happy {
    @apply px-4 py-1 w-32 rounded-md font-semibold bg-bg-happy-light text-text-dark-happy-light shadow-sm shadow-text-dark-happy-light;
    @apply shadow happy-dark:shadow-bg-happy-light happy-light:shadow-text-dark-happy-light;
}
.button-close-modal-address-happy:active,
.button-close-modal-address-happy:focus {
    @apply bg-border-gray-happy-light;
}

.button-submit-modal-address-happy {
    @apply px-4 py-1 w-32 rounded-md bg-button-yellow-happy-light text-text-dark-happy-light font-semibold;
    @apply shadow-sm happy-dark:shadow-bg-happy-light happy-light:shadow-text-dark-happy-light;
}
.button-submit-modal-address-happy:enabled:active,
.button-submit-modal-address-happy:enabled:focus {
    @apply bg-hover-yellow-happy shadow-sm shadow-text-dark-happy-light;
}
.button-submit-modal-address-happy:disabled {
    @apply bg-border-gray-happy-light;
}

@media screen and (min-width: 660px) {
    .section-content-modal-address-happy {
        @apply w-[40rem];
    }
    .section-form-modal-address-happy {
        @apply grid grid-cols-2;
    }
    .div-group-input-modal-address-happy-company {
        @apply mx-auto;
    }
    .div-group-input-default-modal-address-happy {
        @apply col-span-2;
    }
    .div-group-textarea-modal-address-happy {
        @apply col-span-2;
    }
}

@media (hover: hover) {
    .button-close-modal-address-happy:hover {
        @apply bg-border-gray-happy-light  shadow-sm shadow-text-dark-happy-light;
    }
    .button-submit-modal-address-happy:enabled:hover {
        @apply bg-hover-yellow-happy shadow-sm shadow-text-dark-happy-light;
    }
}

@keyframes shake {
    0% {
        transform: rotate(0deg);
    }
    15% {
        transform: rotate(5deg);
    }
    30% {
        transform: rotate(0eg);
    }
    45% {
        transform: rotate(-5deg);
    }
    60% {
        transform: rotate(0deg);
    }
    75% {
        transform: rotate(-5deg);
    }
    100% {
        transform: rotate(0deg);
    }
}
.shake {
    @apply border-header-red-happy;
    animation: shake 0.5s ease-in-out;
}
</style>
